import { Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch";
import { fieldsStateActions } from "../../../../../store/thunks/fieldStatesThunks";
import { isDimension, isMeasure } from "../../../../../utilities/fieldsHelper.ts";
import type { ShowFieldOptionsSettings } from "../../../../viewer/common/Types.ts";
import DropFieldContainer from "../../../common/fields/DropFieldContainer.tsx";
import { createColumnField } from "../../../common/utilities/createFields.ts";
import { extractMeta } from "../../../common/utilities/dropFieldContainerHelper.ts";
import { formatDimensionFieldCaption } from "../../../utils/formatDimensionFieldCaptions.tsx";
import ColumnFieldOptionPopup from "./ColumnFieldOptionPopup.tsx";

import { useSelector } from "react-redux";
import type { FieldConfiguration } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  DraggableFieldType,
  DraggableMetaType,
  FieldWrapper,
  ReportField,
} from "../../../../../../../../../api/types/svTypes.ts";
import { AreaItemType } from "../../../../../../../../../api/types/svTypes.ts";
import { selectColumns, selectConditions, selectRows } from "../../../../../store/selectors/fieldsStateSelectors";
import { getDimensionFields } from "../../../../../utilities/isConfigurationValid.ts";
import OtherOptionsWrapper from "../../../common/fields/field-options/OtherOptionsWrapper.tsx";
import SuppressBlankValues from "../../../common/fields/field-options/SuppressBlankValues.tsx";
import FieldOptionPopupContainer from "../../../common/fields/measures/FieldOptionPopupContainer.tsx";
import MeasureFieldOptions from "../../../common/fields/measures/MeasureFieldOptions.tsx";
import { canDropColumnItem, MaxColsNumber } from "./canDropColumnItem.ts";

interface Props {
  readonlyMode: boolean;
}

export const ColumnsDropFieldsContainer = ({ readonlyMode }: Props) => {
  const conditionsAreaValues = useSelector(selectConditions);
  const columnsAreaValues = useSelector(selectColumns);
  const rowsAreaValues = useSelector(selectRows);
  const dispatch = useAppDispatch();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentFieldId, setCurrentFieldId] = useState<string>();

  const currentField = useMemo(() => {
    if (!currentFieldId) {
      return undefined;
    }
    return columnsAreaValues.find((f) => f.config.guid === currentFieldId);
  }, [currentFieldId, columnsAreaValues]);

  const columnsRef = useRef<ReportField[]>([]);
  useEffect(() => {
    columnsRef.current = columnsAreaValues;
  }, [columnsAreaValues]);

  const rowsRef = useRef<ReportField[]>([]);
  useEffect(() => {
    rowsRef.current = rowsAreaValues;
  }, [rowsAreaValues]);

  const showOptions = useCallback((settings: ShowFieldOptionsSettings<ReportField>) => {
    setCurrentFieldId(settings.field.config.guid);
    setAnchorEl(settings.ref);
  }, []);

  const canDropItem = useCallback(
    (item: FieldWrapper<ReportField> | DraggableMetaType) =>
      canDropColumnItem(item, columnsRef.current, rowsRef.current),
    []
  );

  const updateItemConfig = useCallback(
    (field: ReportField, changes: Partial<FieldConfiguration>) => {
      dispatch(fieldsStateActions.updateColumnConfig({ field, changes }));
    },
    [dispatch]
  );

  const saveChanges = useCallback(
    (changes: Partial<FieldConfiguration>) => {
      if (currentField === undefined) {
        return;
      }
      updateItemConfig(currentField, changes);
    },
    [updateItemConfig, currentField]
  );

  const handleSaveChanges = useCallback(
    (changes: Partial<FieldConfiguration>, causeClosing: boolean) => {
      saveChanges(changes);
      if (causeClosing) {
        setAnchorEl(null);
      }
    },
    [saveChanges]
  );

  const handleColumnFieldCustomLabelChange = (name: string | undefined) => {
    if (currentField) {
      updateItemConfig(currentField, { customLabel: name });
    }
  };

  const addItem = useCallback(
    (item: ReportField, index: number) => {
      dispatch(fieldsStateActions.addColumn({ field: item, index }));
    },
    [dispatch]
  );

  const removeItem = useCallback(
    (item: ReportField) => {
      dispatch(fieldsStateActions.removeColumn(item));
    },
    [dispatch]
  );

  const moveItem = useCallback(
    (item: ReportField, newIndex: number) => {
      dispatch(fieldsStateActions.moveColumn({ field: item, newIndex }));
    },
    [dispatch]
  );

  const handleOnRemove = useCallback(() => {
    setAnchorEl(null);
    if (currentField !== undefined) {
      dispatch(fieldsStateActions.removeColumn(currentField));
    }
  }, [currentField, dispatch]);

  const handleOnCancel = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
      <DropFieldContainer
        title="Columns"
        maxFields={MaxColsNumber}
        areaFieldType={AreaItemType.COLUMNS}
        acceptedDropTypes={[
          AreaItemType.DIMENSION,
          AreaItemType.ROWS,
          AreaItemType.CONDITIONS,
          AreaItemType.VALUES,
          AreaItemType.MEASURE,
        ]}
        fields={columnsAreaValues}
        getKeyValue={getKeyValue}
        canDropItem={canDropItem}
        formatCaption={formatDimensionFieldCaption}
        onAddItem={addItem}
        onRemoveItem={removeItem}
        onMoveItem={moveItem}
        showOptions={showOptions}
        createListItem={createColumnField}
        readonly={readonlyMode}
      />
      {anchorEl && currentField && isDimension(currentField) && (
        <ColumnFieldOptionPopup
          anchorEl={anchorEl}
          field={currentField}
          onUpdateConfiguration={handleSaveChanges}
          cancel={handleOnCancel}
          onRemove={handleOnRemove}
          onUpdateCustomLabel={handleColumnFieldCustomLabelChange}
          readonly={readonlyMode}
        />
      )}
      {anchorEl && isMeasure(currentField) && currentField.meta.capabilities.isScalar && (
        <FieldOptionPopupContainer anchorEl={anchorEl} cancel={handleOnCancel}>
          <MeasureFieldOptions
            field={currentField}
            dimensionFields={getDimensionFields(rowsAreaValues.concat(columnsAreaValues))}
            conditions={conditionsAreaValues}
            otherOptions={
              <OtherOptionsWrapper>
                <SuppressBlankValues
                  field={currentField}
                  onChanged={saveChanges}
                  showExcludeZeroValues={currentField.config.hideBlankValues === true}
                  readonly={readonlyMode}
                />
              </OtherOptionsWrapper>
            }
            cancel={handleOnCancel}
            saveChanges={handleSaveChanges}
            onRemove={handleOnRemove}
            onCalculationItemClick={undefined}
            canAddCustomField={false}
            readonly={readonlyMode}
          />
        </FieldOptionPopupContainer>
      )}
    </Box>
  );
};

export default ColumnsDropFieldsContainer;

function getKeyValue(field: FieldWrapper<DraggableFieldType> | DraggableMetaType) {
  return extractMeta(field)?.name ?? "";
}
